import { useEffect, useState } from 'react'
import { doc } from 'firebase/firestore'
import { onSnapshot } from '@/lib/metered'
import { db } from '@/lib/firebase'
import stamp from '@/data/time-export-stamp.json'

/**
 * Whether the export shipped in this build has been loaded into the tracker yet.
 *
 * The export is a committed file, so it only reaches the tracker through a deploy, and
 * loading it into Firestore is a button somebody has to press. Nothing prompts them to —
 * which is the same shape as the drift this whole panel exists to fix, one level up.
 *
 * The stamp is a handful of numbers rather than the export itself, so this costs a static
 * import of about a hundred bytes instead of the whole file, and the comparison is against
 * the summary document the home page already reads.
 */

export type TimeExportStamp = {
  entries: number
  hours: number
  latest: string | null
  generatedAt: string
}

export const TIME_EXPORT = stamp as TimeExportStamp

/** Both figures are stored rounded to two places, so anything below this is float dust. */
const EPSILON = 0.005

/**
 * `undefined` means the summary document has not been read yet, and is deliberately not
 * treated as "nothing published". Guessing during that first render would put a badge on
 * every admin's bell on every page load, and a badge that is usually wrong is one nobody
 * looks at.
 */
export function exportPending(
  exportHours: number,
  publishedHours: number | null | undefined,
): boolean {
  if (publishedHours === undefined) return false
  if (exportHours <= 0) return false
  if (publishedHours === null) return true
  return Math.abs(exportHours - publishedHours) > EPSILON
}

export function useTimeExportNotice(): {
  pending: boolean
  publishedHours: number | null | undefined
} {
  const [publishedHours, setPublished] = useState<number | null | undefined>(undefined)

  useEffect(
    () =>
      onSnapshot(
        doc(db, 'project', 'summary'),
        (snap) => {
          setPublished(snap.exists() ? ((snap.data().hoursLogged as number) ?? null) : null)
        },
        // A read that fails is not evidence either way, so it stays unknown rather than
        // becoming a notice about an import that may not be due.
        () => setPublished(undefined),
      ),
    [],
  )

  return { pending: exportPending(TIME_EXPORT.hours, publishedHours), publishedHours }
}
